import { getProducedTdhDataSets } from "./getProducedTdhDataSets";
import type {
  MultiScenarioPumpChartContext,
  MultiScenarioPumpChartData
} from "./d";
import { getOperatingPoints } from "./getOperatingPoints";
import { getMultiScenarioProducedTdhDensityCorrectionRatio } from "./getProducedTdhDensityCorrectionRatio";
import { getRequiredTdhDataSets } from "./getRequiredTdhDataSets";
import type { ConvertableUnit } from "@/libs/units";
import { BarrelsPerDay } from "@/libs/units/flowRate";
import { LargeLengthUnits } from "@/libs/units/length";
import {
  PoundsPerSquareInchAbsolute,
  PoundsPerSquareInchDifferential,
  PoundsPerSquareInchGauge
} from "@/libs/units/pressure";
import type { ScenarioMap } from "@/components/types";

export const getMultiScenarioPumpChartData = (
  scenarios: Array<ScenarioMap[string]>,
  targetLiquidFlowUnit: ConvertableUnit,
  targetLargeLengthUnit: ConvertableUnit,
  targetPressureUnit: ConvertableUnit,
  areScenariosEspShore: boolean
): MultiScenarioPumpChartData => {
  // Setup our x and y units and labels. The target units
  // are determined from the sizing in this case since scenarios
  // can have different unit sets
  const targetXUnitLabel =
    targetLiquidFlowUnit.inSituLabel ||
    targetLiquidFlowUnit.shortLabel ||
    targetLiquidFlowUnit.label;
  const baseYUnit = areScenariosEspShore
    ? LargeLengthUnits.Feet
    : PoundsPerSquareInchAbsolute;
  let targetYUnit = areScenariosEspShore
    ? targetLargeLengthUnit
    : targetPressureUnit;
  let targetYUnitForDisplay = targetYUnit;
  if (
    targetYUnit === PoundsPerSquareInchAbsolute ||
    targetYUnit === PoundsPerSquareInchGauge
  ) {
    targetYUnitForDisplay = PoundsPerSquareInchDifferential;
  }
  // Never want to display PSIG on chart
  if (targetYUnit === PoundsPerSquareInchGauge) {
    targetYUnit = PoundsPerSquareInchAbsolute;
  }
  const targetYUnitLabel =
    targetYUnitForDisplay.inSituLabel ||
    targetYUnitForDisplay.shortLabel ||
    targetYUnitForDisplay.label;

  const densityCorrectionRatioByScenarioIndex = scenarios.map(scenario =>
    getMultiScenarioProducedTdhDensityCorrectionRatio(
      scenario,
      areScenariosEspShore
    )
  );

  const context: MultiScenarioPumpChartContext = {
    scenarios,
    densityCorrectionRatioByScenarioIndex,
    baseXUnit: BarrelsPerDay,
    targetXUnit: targetLiquidFlowUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitForDisplay,
    targetYUnitLabel,
    areScenariosEspShore
  };
  const producedTdhDataSets = getProducedTdhDataSets(context);
  const requiredTdhDataSets = getRequiredTdhDataSets(context);
  const operatingPoints = getOperatingPoints(context);

  return {
    context,
    producedTdhDataSets,
    requiredTdhDataSets,
    operatingPoints
  };
};
